import {GetFingerprint} from "./fingerprint/fingerprintjs";

import { ChallengerConf } from "./conf";
import { ChallengerEvents } from "./events";
import { Cdata, ChallengeType, ChallengerData, HttpStatusCode } from "./structs";
import { Utils } from "./utils";

import { Challenge } from "./challenges/challenge";
import { PowChallenge } from "./challenges/pow";
import { CaptchaChallenge } from "./challenges/captcha";
import { SmspinChallenge } from "./challenges/smspin";
import { ViberpinChallenge } from "./challenges/viberpin";
import { HoneypotChallenge } from "./challenges/honeypot";
import { CombinedChallenge } from "./challenges/combined";

// ChallengerCore - hosts the processes associated with fetching the Challenge, the timers associated with refreshing the Challenge, and hosting data shared between Challenges.
export class ChallengerCore {
    conf: ChallengerConf;
    private challengerDiv: HTMLElement | null;

    private events: ChallengerEvents;

    private cpid: string;
	private clientId: string | undefined;
    private challenge: Challenge | null;
    private isRenderable: boolean;

    private refreshChallenge: number;
    private refreshOnFailCount = 0;

    private challengeRequestOut: boolean;

    private static CLIENT_ID_SEPARATOR = ":";

    private isReadyCallback?: ((isReady: boolean, eventData?: {
		pid: string,
		clientId: string,
		ctype?: number,
		ctypeName?: string,
		ctypesCombined?: {ctype: number, ctypeName: string}[]
	}) => void);

    constructor(conf: ChallengerConf, cpid: string, cdata = "", mdata = "") {
        if (!Utils.isUuid(cpid)) { throw Error("Provided CPID is not in the UUID format.  All CPIDs are in UUID format."); }
        this.conf = conf;
        const pidClientIdTuple = this.cpidToClientIdTuple(cpid);
        this.cpid = pidClientIdTuple[0];
        this.clientId = pidClientIdTuple[1];
        this.events = new ChallengerEvents();
        if (cdata && this.setCoreChallenge(cdata, 200, mdata)) { return; }
        this.loadChallenge();
    }

    // stop - Derenders the current Challenge, and stops the automatic processes associated with this ChallengerCore, preparing it for deletion.
    stop(): void {
        this.derender(false);
        this.challenge = null;
        window.clearTimeout(this.refreshChallenge);
    }

    static makeChallenge(ctype: ChallengeType, core: ChallengerCore, data: Cdata, status: number, mdata: string): Challenge {
        let challenge: Challenge;
        switch (ctype) {
            case ChallengeType.CAPTCHA: {
                challenge = new CaptchaChallenge(core, data, status, mdata);
                break;
            }
            case ChallengeType.POW: {
                challenge = new PowChallenge(core, data, status, mdata);
                break;
            }
            case ChallengeType.SMS: {
                challenge = new SmspinChallenge(core, data, status, mdata);
                break;
            }
            case ChallengeType.VIBER: {
                challenge = new ViberpinChallenge(core, data, status, mdata);
                break;
            }
            case ChallengeType.HONEYPOT: {
                challenge = new HoneypotChallenge(core, data, status, mdata);
                break;
            }
            case ChallengeType.COMBINED: {
                challenge = new CombinedChallenge(core, data, status, mdata);
                break;
            }
            default: {
                challenge = new Challenge(core, data, status, mdata);
                break;
            }
        }

        return challenge;
    }

    // setCoreChallenge - sets the Challenge variable for this Challenger core; if this core is currently rendered, it will re-render the Challenge
    private setCoreChallenge(response: string, status: number, mdataPreload?: string): boolean {
        let cdata: Cdata | null;

        let mdata = "";
        if (mdataPreload) { mdata = mdataPreload; }

        try {
            cdata = JSON.parse(response || "") as Cdata;
        }
        catch (e) {
            cdata = null;
        }

        // Hacky workaround for SMSPIN challenge type, where error status is expected depending on flow
        const smsTypeErr = cdata && (((status === HttpStatusCode.BAD_REQUEST || status === HttpStatusCode.FORBIDDEN)
            && (cdata.code == SmspinChallenge.SMS_CODE_PHONE_BLOCKED
            || cdata.code == SmspinChallenge.SMS_CODE_TTL_EXPIRED
            || cdata.code == SmspinChallenge.SMS_CODE_PHONE_REQUIRED))
            || status === HttpStatusCode.TOO_MANY_REQUESTS);

        if (!cdata || (!cdata.result && !smsTypeErr)) {
            if (this.conf.challengerRefreshMSOnFail > 0 && this.conf.challengerRefreshOnFailRetryMaxCount > this.refreshOnFailCount) {
                this.refreshOnFailCount++;
                this.setChallengeTimeout(this.conf.challengerRefreshMSOnFail);
            } else {
                this.refreshOnFailCount = 0;
            }
            return false;
        }

        let challengeType: ChallengeType;
        if (smsTypeErr) {
            challengeType = ChallengeType.SMS;
        } else {
            challengeType = ChallengeType.valueOf(cdata.result.ctype);
        }

        this.challenge = ChallengerCore.makeChallenge(challengeType, this, cdata, status, mdata);

        if (this.conf.challengerRefreshMS > 0 && challengeType != ChallengeType.SMS && challengeType != ChallengeType.VIBER) { this.setChallengeTimeout(this.conf.challengerRefreshMS); }

        if (this.isRenderable) { this.render(); }
        return true;
    }

    // setChallengeTimeout - Sets the next time that the Challenge will automatically refresh
    private setChallengeTimeout(ms: number): void {
        window.clearTimeout(this.refreshChallenge);
        this.refreshChallenge = window.setTimeout(this.loadChallenge.bind(this), ms);
    }

    // sendEventData - Sends ChallengerEvent data to the /p endpoint
    private sendEventData(): void {
        // If cid is empty, do nothing
        const cid = this.getCid();

        if (cid) {
            const body = {
                cid: cid,
                bio: this.events.getBio()
            };

            Utils.cFetch(this.conf.capi, "/p", "POST", body, this.conf.trackingID)
                .catch(() => {
                    //if a network error occurs, currently do nothing.
                });
        }
    }

    private callGeneratorEndpoint(body: Record<string, unknown>) {
        Utils.cFetch(this.conf.capi, "/c", "POST", body, this.conf.trackingID)
        .then((response) => {
            response.text().then((text) => {
                this.challengeRequestOut = false;
                this.setCoreChallenge(text, response.status);
            });
        })
        .catch(() => {
            //this `catch` is called in the event of a network error, or the server is unreachable
            //  below, we trigger a clean retry
            this.challengeRequestOut = false;
            this.setCoreChallenge("", 404);
        });
    }

    // loadChallenge - Gets a new Challenge ID + Challenge Type
    async loadChallenge(): Promise<void> {
        if (this.challengeRequestOut) { return; }
        clearTimeout(this.refreshChallenge);
        this.events.updateTimeToNow();
        const body: Record<string, unknown> = {
            pid: this.cpid,
            lang: this.conf.lang,
            param: this.conf.param
        };
        this.challengeRequestOut = true;

        // Await for agent to load, then get fingerprint data if available
        GetFingerprint().then(({ hashMain, components, hashesOther }) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { canvas, ...prunedComponents } = components;
            const fp = {
                hash: hashMain,
                components: prunedComponents,
                hashesOther: hashesOther
            };
            body["rat"] = fp;
        })
        .catch(() => {
            // On error, rat is set to be null
            body["rat"] = null;
        })
        .then(() => {
            // No matter what, call endpoint to generate challenge
            this.callGeneratorEndpoint(body);
        });
    }

    // getCid - Returns the CID of the challenger.cdata variable
    getCid(): string {
        return this.challenge?.cdata && this.challenge?.cdata?.result ? this.challenge?.cdata.result.cid : "";
    }

    // getCtype - Returns the CTYPE of the challenger.cdata variable
    getCtype(): number {
        return this.challenge?.cdata ? this.challenge?.cdata.result.ctype : -1;
    }

    // render - creates the HTML elements of Challenger on the page, executes the Challenge, 
    async render(challengerDivCode?: string, isReadyCallback?: (isReady: boolean, eventData?: {
		pid: string,
		clientId: string,
		ctype?: number,
		ctypeName?: string,
		ctypesCombined?: {ctype: number, ctypeName: string}[]
	}) => void): Promise<boolean> {
        // check if the <div> with the provided code exists on the page.
        if (!this.challengerDiv || challengerDivCode) { this.challengerDiv = this.tryGetChallengerDiv(challengerDivCode ?? ""); }

        if (isReadyCallback) this.isReadyCallback = isReadyCallback;

        this.isRenderable = true;
        const isChallengeReady = !!(this.challenge?.cdata);

        if (isChallengeReady) {
            this.unsetHtml();
            //attempt to render the Challenge.  If it fails to render, try the indicated number of times.
            for (let i = 0; i < this.conf.challengeRenderRetryMaxCount; i++) {
                if (await this.challenge?.render(this.challengerDiv)) {
                    break;
                }
                // if the Challenge failed to render, wait the provided amount of time, then retry
                await Utils.sleep(this.conf.challengeRenderRetryMS);
            }
        }
        if (isChallengeReady) {
            this.sendEventData();
            this.isReadyCallback?.(
                isChallengeReady,
                {
                    pid: this.cpid,
                    clientId: this.clientId ? this.clientId : "",
                    ctype: this.challenge?.cdata?.result?.ctype,
                    ctypeName: ChallengeType.nameOf(
                        this.challenge?.cdata?.result?.ctype ? this.challenge?.cdata?.result?.ctype : -1
                    ),
                    ctypesCombined: this.challenge instanceof CombinedChallenge ? (this.challenge as CombinedChallenge).getCombinedCtypes() : undefined
                }
            );
        } else {
            this.isReadyCallback?.(
                isChallengeReady,
                {
                    pid: this.cpid,
                    clientId: this.clientId ? this.clientId : ""
                }
            );
            this.loadChallenge();
        }

        return true;
    }

    // derender - removes the HTML elements of Challenger from the page, and gets a new Challenge
    derender(refreshChallenge = true): void {
        this.isRenderable = false;
        this.challengerDiv = null;
        this.unsetHtml();
        if (refreshChallenge) { this.loadChallenge(); }
    }

    // unsetHTML - Removes the HTML elements in the challengerDiv, so they can be reset
    private unsetHtml(): void {
        if (this.challengerDiv) { this.challengerDiv.innerHTML = ""; }
    }

    // getData - Returns the data for this instances of Challenger
    getData(): ChallengerData {
        // Previously, this was attached to all forms' "Submit" buttons.  However,
        // with SPAs, there may not be an explicit "Submit" button.  So, when the
        // ChallengerData is requested from the runner, the event data will be submitted.
        this.sendEventData();
        let cid = "";
        let cres = "";
        let block_submit = false;
        if (!!this.challenge && !!this.challenge.cdata) {
            cid = this.getCid();
            const [challenge_result, should_block] = this.challenge.getChallengeResult();
            cres = challenge_result;
            block_submit = should_block;
        }
        const challengerData = new ChallengerData(
            this.conf.trackingID,
            cid,
            cres,
            this.cpid,
            block_submit,
        );
        return challengerData;
    }

    // tryGetChallengerDiv - Gets the Challenger <div> element for this Challenge
    private tryGetChallengerDiv(challengerDivCode: string): HTMLElement {
        const challengerDiv = window.document.getElementById("challenger_" + challengerDivCode) || window.document.getElementById("challenger");
        if (!challengerDiv) {
            throw Error("Indicated Challenger <div> does not exist on page.");
        }
        return challengerDiv;
    }

    // cpidToClientIdTuple - Tries to get the clientId if its appended to the cpid
    private cpidToClientIdTuple(cpid: string): [string, string | undefined] {
        let clientId: string | undefined;

        const splitted = cpid.split(ChallengerCore.CLIENT_ID_SEPARATOR, 2);
        if (splitted.length == 2) {
            clientId = splitted[1];
        }

        return [splitted[0], clientId];
    }
}
